
import React, { useCallback } from 'react'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
import { withNodeOptimization } from '../hoc/withNodeOptimization'

// ── Props ───────────────────────────────────────────────────────────────────

interface GalleryGroupProps {
  id: string
  title?: string
  selected?: boolean
  width?: number
  height?: number
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
  childCount?: number
  mediaType?: string
}

// ── Main component ──────────────────────────────────────────────────────────

function GalleryGroupComponent({
  id,
  title,
  selected = false,
  width,
  height,
  onStateChange,
  state = {},
  childCount = 0,
  mediaType,
}: GalleryGroupProps) {
  const seriesName = (state.seriesName as string) || title || 'Gallery'
  const columns = (state.columns as number) || 10
  const cellSize = (state.cellSize as number) || 200
  const collapsed = (state.collapsed as boolean) || false

  const mediaLabel = mediaType || 'image'
  const itemLabel = childCount === 1 ? mediaLabel : `${mediaLabel}s`

  const handleToggleCollapse = useCallback(() => {
    onStateChange?.((prev) => ({ ...prev, collapsed: !prev.collapsed }))
  }, [onStateChange])

  if (collapsed) {
    return (
      <div
        className={cn(
          'rounded-2xl border-2 border-dashed transition-colors',
          selected ? 'border-blue-500 bg-blue-500/5' : 'border-border/40 bg-muted/10',
        )}
        style={{ width: width ?? columns * cellSize + 40 }}
        data-node-id={id}
      >
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-2xl',
          selected ? 'bg-blue-500/10' : 'bg-popover/80',
        )}>
          <Icon name="layout-grid" size="sm" className="text-muted-foreground shrink-0" />
          <span className="text-xs font-medium text-foreground truncate">{seriesName}</span>
          <span className="text-[10px] text-muted-foreground tabular-nums ml-auto">
            {childCount} {itemLabel}
          </span>
          <button
            onClick={handleToggleCollapse}
            className="p-0.5 rounded hover:bg-muted transition-colors shrink-0"
          >
            <Icon name="chevron-right" size="sm" className="text-muted-foreground" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'rounded-2xl border-2 border-dashed transition-colors',
        selected ? 'border-blue-500 bg-blue-500/5' : 'border-border/40 bg-muted/10',
      )}
      style={{
        width: width ?? columns * cellSize + 40,
        minHeight: height ?? 240,
      }}
      data-node-id={id}
    >
      {/* Header bar */}
      <div className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-t-2xl border-b border-border/30',
        selected ? 'bg-blue-500/10' : 'bg-popover/80',
      )}>
        <Icon name="layout-grid" size="sm" className="text-muted-foreground shrink-0" />
        <span className="text-xs font-medium text-foreground truncate">{seriesName}</span>
        {mediaType && (
          <span className={cn(
            'text-[9px] px-1 py-0.5 rounded capitalize shrink-0',
            mediaType === 'image' ? 'bg-blue-500/10 text-blue-400' :
            mediaType === 'video' ? 'bg-purple-500/10 text-purple-400' :
            'bg-muted text-muted-foreground',
          )}>
            {mediaType}
          </span>
        )}
        <span className="text-[10px] text-muted-foreground tabular-nums ml-auto shrink-0">
          {childCount} {itemLabel}
        </span>
        <button
          onClick={handleToggleCollapse}
          className="p-0.5 rounded hover:bg-muted transition-colors shrink-0"
        >
          <Icon name="chevron-down" size="sm" className="text-muted-foreground" />
        </button>
      </div>

      {/* Empty state */}
      {childCount === 0 && (
        <div className="flex items-center justify-center py-12 text-xs text-muted-foreground">
          No images yet. Connect a Batch Generator to start.
        </div>
      )}
    </div>
  )
}

export const GalleryGroup = withNodeOptimization(GalleryGroupComponent, {
  compareProps: ['id', 'title', 'selected', 'state', 'childCount', 'width', 'height', 'mediaType'],
})
